<template>
  <div class="wechat-material-upload">
    <div class="material-assets">
      <div v-for="(item, index) in displayList" :key="materialItemKey(item, index)" class="material-asset">
        <div class="upload-tile is-preview">
          <img v-if="materialPreviewUrl(item)" :src="materialPreviewUrl(item)" class="material-preview" />
          <div v-else class="material-placeholder">图片</div>
        </div>
        <button type="button" class="material-remove" :disabled="operationDisabled" title="删除图片" @click.stop="$emit('remove', index)">
          <i class="el-icon-close"></i>
        </button>
      </div>
      <button v-if="showAddTile" type="button" class="upload-tile" :disabled="operationDisabled" @click="$emit('pick')">
        <i class="el-icon-plus"></i>
        <div class="material-title">添加图片</div>
      </button>
    </div>
    <div class="material-copy">
      <div class="material-head">
        <div class="material-title">{{ label }}</div>
        <el-tag class="material-status" size="mini" :type="statusTag.type">{{ statusTag.text }}</el-tag>
      </div>
      <div v-if="fileName" class="material-file">{{ fileName }}</div>
      <div v-if="materialCountText" class="material-file">{{ materialCountText }}</div>
      <div v-if="tip" class="material-tip">{{ tip }}</div>
      <div class="base_tips mt5">支持 JPG、PNG。</div>
      <div v-if="recognizedAt" class="material-tip">
        识别时间：{{ recognizedAt }}
      </div>
    </div>
  </div>
</template>

<script>
import { materialText } from "../utils/wechatApplymentForm";

export default {
  name: "WechatMaterialUpload",
  props: {
    label: {
      type: String,
      required: true,
    },
    tip: {
      type: String,
      default: "",
    },
    value: {
      type: [Object, Array, String],
      default() {
        return {};
      },
    },
    recognizable: {
      type: Boolean,
      default: false,
    },
    syncing: {
      type: Boolean,
      default: false,
    },
    recognizing: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    material() {
      return this.value || {};
    },
    materialList() {
      if (Array.isArray(this.material)) return this.material;
      return [];
    },
    firstMaterial() {
      if (this.materialList.length) {
        return this.materialList.find((item) => item && (item.url || item.base_url || item.file_url || item.thumb_url)) || {};
      }
      return this.material;
    },
    displayList() {
      if (this.materialList.length) return this.materialList.filter((item) => item && (item.asset_id || item.url || item.file_id || item.media_id));
      return this.hasSingleMaterial ? [this.material] : [];
    },
    maxCount() {
      return Math.max(Number(this.max) || 1, 1);
    },
    showAddTile() {
      if (this.multiple) return this.displayList.length < this.maxCount;
      return this.displayList.length === 0;
    },
    hasSingleMaterial() {
      return !!(
        this.material &&
        !Array.isArray(this.material) &&
        (this.material.asset_id || this.material.url || this.material.file_id || this.material.media_id)
      );
    },
    previewUrl() {
      return this.firstMaterial.url || this.firstMaterial.base_url || this.firstMaterial.file_url || this.firstMaterial.thumb_url || "";
    },
    hasMaterial() {
      if (this.materialList.length) {
        return this.materialList.some((item) => item && (item.asset_id || item.url || item.file_id || item.media_id));
      }
      return !!(this.material.asset_id || this.material.url || this.material.file_id || this.material.media_id);
    },
    busy() {
      return this.syncing || this.recognizing;
    },
    operationDisabled() {
      return this.disabled || this.busy;
    },
    isRecognized() {
      if (this.materialList.length) {
        return this.materialList.some((item) => item && (item.recognized_at || item.recognize_status === "recognized"));
      }
      return !!(this.material.recognized_at || this.material.recognize_status === "recognized");
    },
    fileName() {
      if (this.displayList.length) {
        return this.displayList
          .map((item) => (item && (item.name || item.file_name)) || "")
          .filter(Boolean)
          .slice(0, 2)
          .join("、");
      }
      return this.material.name || this.material.file_name || "";
    },
    materialCountText() {
      if (!this.multiple) return "";
      return `已上传 ${this.displayList.length}/${this.maxCount} 张`;
    },
    recognizedAt() {
      return this.material.recognized_at || "";
    },
    statusTag() {
      if (this.materialList.length) {
        if (this.materialList.some((item) => item && item.error)) {
          return { text: materialText(this.materialList), type: "danger" };
        }

        if (this.hasMaterial) {
          return { text: "已上传", type: "primary" };
        }
      }

      if (this.material.error) {
        return { text: materialText(this.material), type: "danger" };
      }

      if (this.material.recognize_status === "recognizing" || this.material.ocr_status === "recognizing" || this.recognizing) {
        return { text: "识别中", type: "warning" };
      }

      if (this.isRecognized) {
        return { text: "已识别，请核对", type: "success" };
      }

      if (this.material.recognize_status === "failed" || this.material.ocr_status === "failed") {
        return { text: "识别失败，请手动填写", type: "danger" };
      }

      if (this.hasMaterial) {
        return { text: "已上传", type: "primary" };
      }

      return { text: "待上传", type: "info" };
    },
  },
  methods: {
    materialPreviewUrl(material = {}) {
      return material.url || material.base_url || material.file_url || material.thumb_url || "";
    },
    materialItemKey(material = {}, index) {
      return material.asset_id || material.media_id || material.file_id || material.url || index;
    },
  },
};
</script>

<style lang="less" scoped>
.wechat-material-upload {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 12px;

  .material-assets {
    display: flex;
    flex: 0 0 120px;
    flex-direction: column;
    gap: 8px;
  }

  .material-asset {
    position: relative;
    width: 120px;
    height: 100px;
  }

  .upload-tile {
    position: relative;
    display: flex;
    width: 120px;
    height: 100px;
    flex: 0 0 120px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    background: #fafafa;
    color: #303133;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    i {
      margin-bottom: 6px;
      color: #006aff;
      font-size: 24px;
    }

    &.is-preview {
      padding: 0;
      cursor: default;
    }
  }

  .material-preview {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    object-fit: cover;
  }

  .material-placeholder {
    color: #909399;
    font-size: 12px;
  }

  .material-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.58);
    color: #fff;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .material-status {
    flex: 0 0 auto;
    white-space: normal;
  }

  .material-title {
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
  }

  .material-head {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    justify-content: space-between;
  }

  .material-copy {
    flex: 1;
    min-width: 0;
    padding-top: 2px;
  }

  .material-file,
  .material-tip {
    margin-top: 6px;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
  }
}
</style>
